/**
 * Shared window chrome — the frame every full-screen game panel wears.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * ─── What this replaces ─────────────────────────────────────────────────────
 * Six panels (vendor, quest, banker, currency shop, black market, world map)
 * each hand-wrote the SAME five declaration strings into `style.cssText`:
 *
 *   root.style.cssText  = 'display:none;z-index:1001;background:rgba(6,4,2,.78)'
 *   panel.style.cssText = 'padding:18px;width:min(94vw,560px);max-height:82vh;...'
 *   close.style.cssText = 'position:absolute;top:6px;right:10px;font-size:1.5rem;...'
 *   title.style.cssText = 'color:var(--brass-hot);font-size:1.5rem;font-weight:700;...'
 *   sub.style.cssText   = 'color:var(--warn);font-size:.72rem;...'
 *
 * Character for character, with the panel's own max width the only real
 * difference. Six copies of a rule is six places to miss when the look
 * changes, and `styles.tokens.test.ts` exists because ONE of those copies once
 * named a token that did not exist and shipped an invisible button — the
 * failure mode of inline style strings is silence.
 *
 * ─── The width is the parameter ─────────────────────────────────────────────
 * `.bv-win` reads `--win-w`, which each panel sets inline. That is the only
 * thing the six genuinely disagreed about, so it is the only thing left at the
 * call site.
 *
 * ─── Deliberately NOT here ──────────────────────────────────────────────────
 * Each panel's own layout — the trade grid, the map's plate, the vocation
 * tiles. Those belong to their own slices. This file is the frame, the title,
 * the close cross, the message lines and the buttons: the parts that are
 * supposed to look identical everywhere and currently only do by coincidence.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* ── The scrim ────────────────────────────────────────────────────────────
   Full-screen dim behind a window. z-index 1001 puts it over the world and
   under the player frame (1002), which is what keeps the action bar usable
   with a window open — a button that can open a window but never close it is
   the bug that ordering prevents.

   display is deliberately NOT set here. Every panel opens and closes by
   writing style.display, and its own isOpen() reads that back; moving the
   toggle into a class would mean touching six open/close pairs and their
   tests for no visual gain. The inline display:none stays; everything that
   is purely a LOOK moved here. */
.bv-scrim {
  position: fixed; inset: 0; z-index: 1001;
  display: flex; align-items: center; justify-content: center;
  background: rgba(6,4,2,0.78);
}

/* ── The frame ────────────────────────────────────────────────────────────
   Pair with .panel and .grained from app.css, which carry the surface
   gradient, the border, the radius and the dirt overlay. This adds only what
   those two do not know about: the box the window occupies on screen.

   --win-w is set inline per panel (the map wants 900px, the banker 480px).
   Defaulted so a panel that forgets it still renders a sane window instead of
   collapsing to its content. */
.bv-win {
  position: relative;
  padding: 18px;
  width: min(94vw, var(--win-w, 560px));
  max-height: 86vh;
  overflow-y: auto;
}

/* The close cross is the shared .bv-close (close.css.ts); the window class only
   marks it as this frame's cross. */

.bv-win-title {
  margin: 0;
  font: 700 1.5rem/1.1 var(--font-display); letter-spacing: 0.02em;
  text-transform: uppercase; color: var(--brass-hot);
}
/* The line under a title: a shout in small caps, not a sentence. Warn-coloured
   because in every window it says what the place is FOR, and that framing is
   always a caution in this game (Gold only, prices are final, the road out
   there is not safe). */
.bv-win-sub {
  margin: 2px 0 6px;
  font: 0.72rem var(--font-technical); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--warn);
}
.bv-win-body { margin-top: 10px; }

/* ── Message lines ────────────────────────────────────────────────────────
   Three kinds, and they are genuinely different: something went wrong,
   something worked, and something is merely worth knowing. They were three
   different inline strings per panel before, which is how the same failure
   ended up rust-coloured in one window and moss-coloured in another. */
.bv-win-err {
  margin: 8px 0; padding: 7px 10px; border-radius: var(--radius-well);
  font: 0.85rem var(--font-technical);
  color: #ffb9a6; background: rgba(168,69,44,0.16); border: 1px solid rgba(168,69,44,0.5);
}
.bv-win-ok {
  margin: 6px 0; font: 0.82rem var(--font-note); color: var(--moss);
}
.bv-win-note {
  margin: 6px 0; font: 0.8rem var(--font-technical); color: var(--bone-faint);
  letter-spacing: 0.04em;
}
.bv-win-empty {
  padding: 14px 0; font: 0.85rem var(--font-note); color: var(--bone-faint);
}

/* ── Buttons ──────────────────────────────────────────────────────────────
   .bv-act is the neutral one. -go is the commit (brass, dark text), -danger
   the irreversible one, -ghost a cancel. Sized the same so a row of them
   lines up, which is the thing that reads as "one game" rather than "six
   screens by six people". */
.bv-act {
  position: relative; padding: 6px 15px; cursor: pointer; overflow: hidden;
  border: 1px solid #1a1208; border-radius: var(--radius-well);
  background:
    radial-gradient(circle at 7px 50%, rgba(255,222,170,0.5) 0 1.5px, rgba(0,0,0,0.55) 2px, transparent 2.6px),
    radial-gradient(circle at calc(100% - 7px) 50%, rgba(255,222,170,0.5) 0 1.5px, rgba(0,0,0,0.55) 2px, transparent 2.6px),
    radial-gradient(circle at 22% 30%, rgba(255,255,255,0.05) 0 1px, transparent 1.5px) 0 0 / 9px 7px,
    radial-gradient(circle at 70% 70%, rgba(0,0,0,0.2) 0 1px, transparent 1.5px) 0 0 / 11px 8px,
    linear-gradient(180deg, #5c4a30 0%, #3d2f1c 48%, #2a2012 100%);
  color: var(--bone);
  font: 700 0.72rem var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  text-shadow: 0 1px 0 rgba(0,0,0,0.7);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.22), inset 0 -2px 0 rgba(0,0,0,0.45),
              inset 0 0 0 1px rgba(171,138,83,0.18), 0 2px 4px rgba(0,0,0,0.5);
  transition: filter 0.12s ease, color 0.12s ease, border-color 0.12s ease,
              box-shadow 0.12s ease, transform 0.08s ease;
}
.bv-act:hover:not(:disabled) {
  filter: brightness(1.18); border-color: var(--brass); color: var(--brass-bright);
}
.bv-act:active:not(:disabled) {
  transform: translateY(1px); filter: brightness(0.92);
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(0,0,0,0.3);
}
.bv-act:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }
.bv-act:disabled { opacity: 0.45; cursor: default; filter: grayscale(0.5); }
.bv-act-go {
  background:
    radial-gradient(circle at 7px 50%, rgba(255,246,222,0.7) 0 1.5px, rgba(60,36,8,0.7) 2px, transparent 2.6px),
    radial-gradient(circle at calc(100% - 7px) 50%, rgba(255,246,222,0.7) 0 1.5px, rgba(60,36,8,0.7) 2px, transparent 2.6px),
    radial-gradient(circle at 22% 30%, rgba(255,255,255,0.12) 0 1px, transparent 1.5px) 0 0 / 9px 7px,
    radial-gradient(circle at 70% 70%, rgba(60,36,8,0.25) 0 1px, transparent 1.5px) 0 0 / 11px 8px,
    linear-gradient(180deg, #ffd08a 0%, var(--brass-hot) 42%, #9a6c2c 100%);
  border-color: #5a3f14; color: #1a1208; text-shadow: 0 1px 0 rgba(255,236,200,0.5);
  box-shadow: inset 0 1px 0 rgba(255,246,222,0.55), inset 0 -2px 0 rgba(88,56,18,0.55),
              0 3px 6px rgba(0,0,0,0.55);
}
.bv-act-go:hover:not(:disabled) { color: #1a1208; border-color: #8a6320; filter: brightness(1.1); }
.bv-act-danger {
  background:
    radial-gradient(circle at 7px 50%, rgba(255,190,170,0.4) 0 1.5px, rgba(0,0,0,0.6) 2px, transparent 2.6px),
    radial-gradient(circle at calc(100% - 7px) 50%, rgba(255,190,170,0.4) 0 1.5px, rgba(0,0,0,0.6) 2px, transparent 2.6px),
    radial-gradient(circle at 22% 30%, rgba(255,255,255,0.05) 0 1px, transparent 1.5px) 0 0 / 9px 7px,
    linear-gradient(180deg, #6a241a 0%, #4a1712 50%, #250b08 100%);
  border-color: #7e2b1f; color: #f0a893;
}
.bv-act-danger:hover:not(:disabled) { color: #ffd3c4; border-color: var(--rust); }
.bv-act-ghost {
  background:
    radial-gradient(circle at 22% 30%, rgba(255,255,255,0.04) 0 1px, transparent 1.5px) 0 0 / 9px 7px,
    linear-gradient(180deg, #1a130c, var(--well));
  border-color: var(--line); color: var(--bone-dim);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.6), 0 1px 0 rgba(255,222,170,0.06);
}
.bv-act-ghost:hover:not(:disabled) { color: var(--bone); }
.bv-act-row { display: flex; gap: 8px; align-items: center; }

/* ── Currency ─────────────────────────────────────────────────────────────
   Cash is bone, Gold is brass — everywhere, so a number's colour alone says
   which currency it is. The Gold ICON (assets/ui, via hud.ts's goldIconHtml)
   sits inline in the same line; this sizes it to the text rather than letting
   each caller guess. */
.bv-win-cash { color: var(--bone); font-variant-numeric: tabular-nums; }
.bv-win-gold { color: var(--brass-hot); font-variant-numeric: tabular-nums; }
/* Amounts: the full figure from tablet width up, K / KK below it (window-chrome.ts compactAmount). */
.bv-amt-short { display: none; }
@media (max-width: 767px) {
  .bv-amt-long { display: none; }
  .bv-amt-short { display: inline; }
}
/* A Gold price ON a gold button ("5000", "120" in the Black Market) was gold
   text and a gold bar on a gold face -- Janeks: "yellow on yellow text". On a
   brass button the amount takes the button's dark ink and the bar gets a dark
   rim so it still reads as metal. */
.bv-act-go .bv-win-gold, .bv-act-go .bv-win-cash { color: inherit; }
.bv-act-go .bv-coin {
  filter: drop-shadow(0 0 1px rgba(26,18,8,0.95)) drop-shadow(0 1px 0 rgba(26,18,8,0.7));
}
/* A disabled commit button stays recognisably brass, dimmed. The shared
   0.45 opacity + grayscale turned it into a black slab on the dark cards. */
.bv-act-go:disabled { opacity: 0.6; filter: saturate(0.45) brightness(0.85); }
.bv-coin {
  width: 1.05em; height: 1.05em; vertical-align: -0.16em;
  margin-right: 0.22em;
  display: inline-block; object-fit: contain;
}

/* Visible to a screen reader, not on screen.
   The Gold coin is aria-hidden (decorative), so the unit word has to survive
   somewhere or the sentence read aloud becomes a bare number. clip-path over
   display:none because a hidden element is not announced at all. */
.bv-sr {
  position: absolute; width: 1px; height: 1px;
  margin: -1px; padding: 0; overflow: hidden;
  clip-path: inset(50%); white-space: nowrap; border: 0;
}
`;
